// Q9 S3 — Pure helper that builds the monolithic VisitReport API payload
// from a FormState. Extracted from VisitReportPage.tsx so the
// VisitReportWizard can also consume it (then scatter into per-section
// PATCH bodies via extractSectionPayload from api/visit-report-api.ts).
//
// TASK-48 S2 — Return type narrowed to `Partial<SubmitInput>` via the
// `satisfies` operator so any future field-name typo here fails tsc
// against the canonical schema in @hcm/core.

import type { SubmitInput } from "@hcm/core";
import type { FormState } from "./visit-report-types.js";
import { numOrNull } from "./visit-report-types.js";

// eslint-disable-next-line max-lines-per-function, complexity, sonarjs/cognitive-complexity -- flat field-by-field payload builder; every branch is one line of "if X copy Y". Sub-builder extraction was considered + rejected because there's no reusable abstraction across the branches. Tracked under TASK-N19 Phase B.
export function buildPayload(id: string, form: FormState): Record<string, unknown> {
  const payload = {
    sr_id: id,
    completed_at: new Date().toISOString(),
    submission_channel: "crm",
    bp_systolic: numOrNull(form.bp_sys),
    bp_diastolic: numOrNull(form.bp_dia),
    bp_position: form.bp_pos || null,
    heart_rate: numOrNull(form.hr),
    spo2_percent: numOrNull(form.spo2),
    temperature_celsius: numOrNull(form.temp),
    blood_glucose_mmol: numOrNull(form.glucose),
    weight_kg: numOrNull(form.weight),
    respiratory_rate: numOrNull(form.rr),
    mobility_status: form.mobility || null,
    mobility_vs_last_visit: form.mobility_vs_last || null,
    fall_during_visit: form.fall_visit === "yes",
    fall_since_last_visit_reported: form.fall_since === "yes",
    fall_count_since_last_visit: form.fall_since === "yes" ? numOrNull(form.fall_count_since_last_visit) : null,
    fall_injury_sustained: form.fall_injury_sustained === "yes",
    fall_injury_description: form.fall_injury_sustained === "yes" ? form.fall_injury_description || null : null,
    near_miss_reported: form.near_miss === "yes",
    patient_expressed_safety_concern: form.patient_safety_concern === "yes",
    medication_error_observed: form.medication_error_observed,
    wandering_behaviour_reported: form.wandering_behaviour_reported,
    escalation_severity: form.escalation ? form.escalation_severity || null : null,
    environment_safety_concern: form.environment_safety_concern,
    home_condition_concern_type: form.environment_safety_concern ? form.home_condition_concern_type || null : null,
    medication_list_verified: form.medication_list_verified,
    pain_present: form.pain_present === "yes",
    pain_vs_last_visit: form.pain_present === "yes" ? form.pain_vs_last || null : null,
    pain_severity: form.pain_present === "yes" ? numOrNull(form.pain_severity) : null,
    pain_location: form.pain_present === "yes" ? form.pain_location || null : null,
    ambulation_status: form.ambulation || null,
    adl_score: form.adl_score !== "" ? Number(form.adl_score) : null,
    continence_status_visit: form.continence || null,
    grooming_status: form.grooming || null,
    medication_admin_capacity: form.med_admin_cap || null,
    cognitive_status: form.cognitive || null,
    cognitive_vs_last_visit: form.cognitive_vs_last || null,
    cognitive_test_used: form.cog_test_used || null,
    cognitive_test_score: form.cog_test_score !== "" ? Number(form.cog_test_score) : null,
    cognitive_assessment_interpretation: form.cog_interpretation || null,
    mood_affect: form.mood || null,
    appetite_today: form.appetite || null,
    breathlessness_present: form.breathlessness_present === "yes",
    breathlessness_severity: form.breathlessness_present === "yes" ? form.breathlessness_severity || null : null,
    breathlessness_timing: form.breathlessness_present === "yes" ? form.breathlessness_timing || null : null,
    eating_status: form.eating_status || null,
    unplanned_weight_loss_reported: form.unplanned_weight_loss_reported === "yes",
    fever_or_night_sweats: form.fever_or_night_sweats === "yes",
    persistent_cough: form.persistent_cough === "yes",
    dizziness_reported: form.dizziness_reported === "yes",
    nausea_or_vomiting: form.nausea_or_vomiting === "yes",
    unusual_fatigue_reported: form.unusual_fatigue_reported === "yes",
    symptom_detail: ((): Record<string, string | undefined> | undefined => {
      const activeDetail = Object.fromEntries(
        (["unplanned_weight_loss_reported","fever_or_night_sweats","persistent_cough",
           "dizziness_reported","nausea_or_vomiting","unusual_fatigue_reported"] as const)
          .filter((k) => form[k] === "yes" && form.symptom_detail[k])
          .map((k) => [k, form.symptom_detail[k]])
      );
      return Object.keys(activeDetail).length > 0 ? activeDetail : undefined;
    })(),
    fluid_intake_adequate: form.fluid_intake || null,
    sleep_quality_reported: form.sleep_quality || null,
    bowel_function: form.bowel_function || null,
    urinary_function: form.urinary_function || null,
    skin_condition: form.skin_condition || null,
    self_care_ability: form.self_care_ability || null,
    communication_ability: form.communication_ability || null,
    patient_engagement_level: form.patient_engagement_level || null,
    overall_patient_status: form.overall_status,
    escalation_flag: form.escalation,
    safeguarding_concern_observed: form.escalation,
    supplementary_notes: form.notes || undefined,
    medication_adherence_log: form.meds.filter((m) => m.drug_name.trim() !== ""),
    carer_recommendation: form.recommendations,
    // TASK-48 S2 — `satisfies` against a Partial<Record<keyof SubmitInput,
    // unknown>> gives compile-time field-name enforcement (typos at any
    // key here will fail tsc) without forcing value-type narrowing.
    // FormState's looser value types (e.g., `bp_pos: string` vs the
    // schema's `bp_position: "sitting" | "standing" | "lying"`) flow
    // through unchanged; submission still goes through the server's
    // Zod parse for value-type validation.
  } satisfies Partial<Record<keyof SubmitInput, unknown>>;
  return payload;
}
